import { AES, RSA } from '@datarock/cryptography';
import { useEffect, useRef, useState } from 'react';
import { createGlobalState } from 'react-hooks-global-state';
import { sendClientPublicKeyEndpoint } from '@datarock/api-client';
import useClientKeys from './useClientKeys';
import useConfigContext from './useConfigContext';
import { useController } from '@data-client/react';
import useSessionId from './useSessionId';
import { withRetry } from './retryUtils';
const { useGlobalState } = createGlobalState({ symmetricKey: null });
let globalState = {
    key: null,
    error: null,
    isLoading: false,
    retryCount: 0
};
let promise = null;
// Function to reset the global state and allow retries
export function resetSymmetricKey() {
    globalState = {
        key: null,
        error: null,
        isLoading: false,
        retryCount: 0
    };
    promise = null;
}
export default function useSymmetricKey() {
    const { rsaDecrypt } = useConfigContext();
    const { keys: clientKeys } = useClientKeys();
    const [sessionId] = useSessionId();
    const [key, setKey] = useGlobalState('symmetricKey');
    const [localState, setLocalState] = useState(globalState);
    const { fetch } = useController();
    const fetchRef = useRef(fetch);
    fetchRef.current = fetch;
    useEffect(() => {
        (async () => {
            // If we already have a key, don't make a new request
            if (key) {
                return;
            }
            if (promise) {
                return;
            }
            if (!(clientKeys === null || clientKeys === void 0 ? void 0 : clientKeys.publicKey)) {
                return;
            }
            if (!sessionId) {
                return;
            }
            setLocalState((prev) => ({ ...prev, isLoading: true, error: null }));
            try {
                promise = withRetry(() => fetchRef.current(sendClientPublicKeyEndpoint, {
                    clientPublicKey: clientKeys.publicKey,
                    sessionId
                }), {
                    maxRetries: 3,
                    baseDelay: 1000,
                    maxDelay: 10000,
                    timeout: 30000
                });
                const response = await promise;
                if (response) {
                    const bytes = await (rsaDecrypt === undefined
                        ? RSA.decrypt(clientKeys.privateKey, response.sessionKey)
                        : rsaDecrypt(clientKeys.privateKey, response.sessionKey));
                    const k = await AES.importKey(bytes);
                    setKey(k);
                    setLocalState({
                        key: k,
                        error: null,
                        isLoading: false,
                        retryCount: 0
                    });
                    globalState = {
                        key: k,
                        error: null,
                        isLoading: false,
                        retryCount: 0
                    };
                }
            }
            catch (err) {
                const error = err instanceof Error ? err : new Error(String(err));
                setLocalState((prev) => ({
                    ...prev,
                    error,
                    isLoading: false
                }));
                globalState = {
                    ...globalState,
                    error,
                    isLoading: false
                };
            }
            finally {
                promise = null;
            }
        })();
    }, [
        clientKeys === null || clientKeys === void 0 ? void 0 : clientKeys.privateKey,
        clientKeys === null || clientKeys === void 0 ? void 0 : clientKeys.publicKey,
        key,
        rsaDecrypt,
        sessionId,
        setKey
    ]);
    // Throw error if there is one
    if (localState.error) {
        throw localState.error;
    }
    return localState.key || key;
}
